import { computed, reactive, ref } from 'vue';
import FormattingService from '@/services/formatting.service';

export default {
  name: 'EventPriceBoxComponent',
  props: {
    event: {
      type: Object,
      required: true,
    },
    userEvents: {
      type: Array,
      required: false,
      default: [],
    },
    user: {
      type: Boolean,
      required: false,
      default: false,
    },
    showCheckout: {
      type: Boolean,
      required: false,
      default: false,
    },
    inPersonOnly: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  setup(props) {
    const state = reactive({
      event: ref(props.event),
      eventReplayForSale: ref(Boolean(props.event.replay.sellable)),
      onSale: computed(() => {
        if (props.event.sale.price) {
          if (props.event.sale.dates.ends_at) {
            return !inThePast(props.event.sale.dates.ends_at);
          }
          return true;
        }
        return false;
      }),
      salePrice: ref(props.event.sale.price),
      reSalePrice: ref(props.event.resale.price),
      hasResale: computed(() => {
        if (props.event.resale.price) {
          if (props.event.resale.dates.ends_at) {
            return !inThePast(props.event.resale.dates.ends_at);
          }
          return true;
        }
        return false;
      }),
      detailsUrl: computed(() => {
        if (props.event.in_person_product_handle) {
          if (props.event.in_person_details_url && !inThePast(props.event.ends_at)) {
            return props.event.in_person_details_url;
          }
        }
        return false;
      }),
      ribbonText: (userEvents) => {
        if (userHasEvent(userEvents)) {
          if (inThePast(state.event.ends_at)) {
            if (userEventIsExpired(userEvents)) {
              return 'buy again';
            }
            // user event is not expired
            return 'active';
          }
          // event is not in the past
          return 'registered';
        }
        // user does not have event
        if (state.onSale) {
          return 'sale';
        }

        return '';
      },
      ribbonBarColorClass: (userEvents) => {
        if (userHasEvent(userEvents)) {
          if (inThePast(state.event.starts_at)) {
            if (userEventIsExpired(userEvents)) {
              return 'koc-event-expired';
            }
            // user event is not expired
            return 'koc-event-active';
          }
          // event is not in the past
          return 'koc-event-active';
        }
        // user does not have event
        if (state.onSale) {
          return 'koc-event-sale';
        }

        return '';
      },
      showRibbonBar: (userEvents) => {
        if (userHasEvent(userEvents)) {
          return true;
        }
        if (state.onSale) {
          return true;
        }

        return false;
      },
      showLoginLink: (userEvents, user) => {
        return !user;
      },
      resaleEligible: (userEvents) => {
        if (userHasEvent(userEvents)) {
          if (inThePast(state.event.ends_at)) {
            if (userEventIsExpired(userEvents)) {
              if (state.eventReplayForSale) {
                if (state.hasResale) {
                  return true;
                }
              }
            }
          }
        }

        return false;
      },
      titleText: (event) => {
        if (inThePast(event.ends_at)) {
          return `full ${event.type} replay`;
        }

        return `${eventDayLength(event)}-day ${event.type} pass`;
      },
      buttonText: (userEvents) => {
        if (!inThePast(state.event.ends_at)) {
          if (userHasEvent(userEvents)) {
            return 'watch now';
          }
          return 'sign me up!';
        }
        // event is in the past
        if (userHasEvent(userEvents)) {
          if (userEventIsExpired(userEvents)) {
            return 'repurchase';
          }
          return 'watch now';
        }
        // user does not have event
        return 'buy this replay now';
      },
      buttonDisabled: (userEvents) => {
        if (!inThePast(state.event.ends_at)) {
          if (userHasEvent(userEvents)) {
            if (!state.event.live_event_url) {
              return true;
            }
          }
        } else {
          // event is in the past
          if (userHasEvent(userEvents)) {
            if (!state.event.replay.url) {
              return true;
            }
          }
          // user does not have event
          if (!state.event.replay.sellable) {
            return true;
          }
        }

        return false;
      },
      buttonUrl: (userEvents) => {
        if (userHasEvent(userEvents)) {
          if (!userEventIsExpired(userEvents)) {
            if (!inThePast(state.event.ends_at)) {
              return state.event.live_event_url;
            }
            return state.event.replay.url;
          }
        }

        return 'checkout';
      },
      subheaderText: (userEvents, user) => {
        if (user) {
          if (userHasEvent(userEvents)) {
            if (inThePast(state.event.ends_at)) {
              if (userEventIsExpired(userEvents)) {
                if (state.eventReplayForSale) {
                  if (state.onSale) {
                    return 'Your Access Has Expired. Extend for A Full Year at This Special Price!';
                  }
                  if (state.hasResale) {
                    return `${state.event.resale.title} Price Expires ${formatDate(state.event.resale.dates.ends_at)}`;
                  }
                  return 'Get Access to This Events Sessions for 1 Full Year!';
                }
                // replay is not for sale
                return 'Access to This Events Session Replays for 1 Full Year—Coming Soon!';
              }
              // user has event and not expired
              return `Replay Access Expires ${formatDate(state.event.replay.dates.expires)}`;
            }
            // user has event and event not in the past
            if (state.event.live_event_url) {
              return 'Your Event Is Going On Now!';
            }
            // event does not have live url yet
            if (state.event.precon.dates.starts) {
              return `Live Event Page Will Open For Viewing On ${formatDate(state.event.precon.dates.starts)}`;
            }
            return `Live Event Page Will Open For Viewing On ${formatDate(state.event.dates.starts)}`;
          }
          // user does not have event
          if (!inThePast(state.event.ends_at)) {
            if (state.onSale) {
              return `${state.event.sale.title} Price Expires ${formatDate(state.event.sale.dates.ends_at)}`;
            }
            return `Don’t Miss Your Chance, Register by ${formatDate(state.event.ends_at)}`;
          }
          // event date is passed
          if (state.eventReplayForSale) {
            if (state.onSale) {
              return `${state.event.sale.title} Price Expires ${formatDate(state.event.sale.dates.ends_at)}`;
            }
            return 'Get Access to This Events Sessions for 1 Full Year!';
          }
          // replay is not for sale
          return 'Access to This Events Session Replays for 1 Full Year—Coming Soon!';
        }
        // no user
        if (!inThePast(state.event.ends_at)) {
          if (state.onSale) {
            return `${state.event.sale.title} Price Expires ${formatDate(state.event.sale.dates.ends_at)}`;
          }
          return `Don’t Miss Your Chance, Register by ${formatDate(state.event.ends_at)}`;
        }
        // event date is passed
        if (state.eventReplayForSale) {
          if (state.onSale) {
            return `${state.event.sale.title} Price Expires ${formatDate(state.event.sale.dates.ends_at)}`;
          }
          return 'Get Access to This Events Sessions for 1 Full Year!';
        }
        // replay is not for sale
        return 'Access to This Events Session Replays for 1 Full Year—Coming Soon!';
      },
    });

    const formatPrice = (price) => {
      return `${price}`;
    };

    const eventDayLength = (event) => {
      const start = new Date(event.starts_at);
      const end = new Date(event.ends_at);
      const diff = end - start;
      const days = Math.ceil(diff / (1000 * 60 * 60 * 24));

      return days;
    };

    const userHasEvent = (userEvents) => {
      if (userEvents.length) {
        return userEvents.some(events => events.event.id === state.event.id);
      }
      return false;
    };

    const getUserEvent = (userEvents) => {
      if (userHasEvent(userEvents)) {
        return userEvents.find(events => events.event.id === state.event.id);
      }
      return null;
    };

    const getUserEventExpiration = (userEvents) => {
      if (userHasEvent(userEvents)) {
        const userEvent = userEvents.find(events => events.event.id === state.event.id);
        return userEvent.event.replay.dates.expires;
      }
      return null;
    };

    const userEventIsExpired = (userEvents) => {
      const expires = getUserEventExpiration(userEvents);
      return expires ? inThePast(expires) : false;
    };

    const inThePast = (datetime) => {
      const date = new Date(datetime);
      const now = new Date();

      return (now > date);
    };

    const eventIsLive = (start, end) => {
      const startDate = new Date(start);
      const endDate = new Date(end);
      const now = new Date();

      return (now >= startDate && now <= endDate);
    };

    const formatDate = (date) => FormattingService.formatDateTimeWithZone(date);

    return {
      state,
      formatPrice,
      inThePast,
      userHasEvent,
      getUserEvent,
      getUserEventExpiration,
      userEventIsExpired,
      eventIsLive
    };
  },
  methods: {
    login: async function() {
      this.$emit('show-login');
    },
    buttonClick: async function(data) {
      if ('checkout' !== data) {
        window.location.href = data;
      } else {
        this.$emit('show-checkout');
      }
    },
  },
};
